import { memo, useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { queryKeys } from '@/utils/queryKeys';
import { preloadOnInteraction } from '@/lib/route-preloader-enhanced';
import { prefetchData } from '@/lib/data-prefetcher';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bell } from 'lucide-react';
import { NotificationItem } from '@/components/molecules/NotificationItem';
import {
  useFlattenedNotifications,
  useUnreadNotificationCount,
  useNotificationMutations,
} from '@/hooks/useNotifications';

import { NotificationRowSkeleton } from '@/components/molecules/skeletons';
import { NOTIFICATIONS, SKELETON } from '@/lib/constants';

interface NotificationDropdownProps {
  children: React.ReactNode;
}

export const NotificationDropdown = memo<NotificationDropdownProps>(
  function NotificationDropdown({ children }) {
    const queryClient = useQueryClient();
    const { 
      notifications, 
      isLoading, 
      fetchNextPage, 
      hasNextPage, 
      isFetchingNextPage 
    } = useFlattenedNotifications(NOTIFICATIONS.DROPDOWN_PAGE_SIZE);
    const { data: unreadCount = 0 } = useUnreadNotificationCount();
    const { markAsRead, markAllAsRead, markGroupAsRead, notificationListViewed, isMarkingAllAsRead } =
      useNotificationMutations();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const handleClose = useCallback(() => {
      setIsOpen(false);
    }, []);

    const handleViewAllPrefetch = useCallback(() => {
      preloadOnInteraction('/notifications');
      prefetchData('/notifications');
    }, []);

    const handleOpenChange = useCallback((open: boolean) => {
      setIsOpen(open);
      if (open) {
        // Optimistically clear badge when user opens the dropdown
        queryClient.setQueryData(queryKeys.notifications.count(), 0);
      } else {
        // Persist the "viewed at" timestamp on the server when dropdown closes
        notificationListViewed();
      }
    }, [queryClient, notificationListViewed]);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = target;
      
      if (scrollHeight - scrollTop <= clientHeight + 100 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    return (
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          className="w-[480px] p-0 bg-card shadow-md border border-border"
          align="start"
          sideOffset={8}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h3 className="font-semibold text-foreground">Notifications</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllAsRead()}
              disabled={!notifications.some(n => !n.read_at) || isMarkingAllAsRead}
              className="text-xs h-7"
            >
              Mark all as read
            </Button>
          </div>

          {/* Content */}
          <ScrollArea 
            className="h-[420px]"
            onScrollCapture={handleScroll}
            ref={scrollRef}
          >
            {isLoading ? (
              <div className="divide-y">
                {Array.from({ length: SKELETON.NOTIFICATION_ROWS }, (_, i) => (
                  <NotificationRowSkeleton key={i} />
                ))}
              </div>
            ) : notifications && notifications.length > 0 ? (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onRead={markAsRead}
                    onGroupRead={markGroupAsRead}
                    onClose={handleClose}
                  />
                ))}
                {isFetchingNextPage && (
                  <div className="py-3">
                    <NotificationRowSkeleton />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Bell className="h-10 w-10 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">
                  No notifications yet
                </p>
              </div>
            )}
          </ScrollArea>

          {/* Footer with View All link */}
          <div className="border-t px-4 py-3 text-center">
            <Link 
              to="/notifications" 
              className="text-sm text-primary hover:underline"
              onClick={handleClose}
              onMouseEnter={handleViewAllPrefetch}
              onFocus={handleViewAllPrefetch}
            >
              View all notifications
            </Link>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);
